import React, { useEffect, useRef, useState } from 'react'
import { Box, IconButton, Typography } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import { Close as CloseIcon, ChevronRight } from '@material-ui/icons'
import PropTypes from 'prop-types'
import clsx from 'clsx'

import { Pill } from './components'

import useAuth from 'hooks/useAuth'
import useBannerOffset from 'hooks/useBannerOffset'

import DpoDayHexagon from 'images/dpoday-hexagon-2027.png'
import DpoDayCuboLogo from 'images/dpoday-cubo-logo.png'

import useStyles from './styles'
import helpers from 'helpers'
import constants from 'constants/index'

const TITLE_MAX_FONT_SIZE = 15
const TITLE_MIN_FONT_SIZE = 10

const DpoDayBanner = ({ variant, daysRemaining, onVisibilityChange }) => {
  const {
    DPO_DAY_STORAGE_KEY_PREFIX,
    DPO_DAY_TARGET_URL,
    DPO_DAY_BANNER_COUNTDOWN_VARIANT,
    DPO_DAY_PILL_COLORS,
    DPO_DAY_BANNER_MIXPANEL_TAG,
  } = constants.dpoday
  const STORAGE_KEY = `${DPO_DAY_STORAGE_KEY_PREFIX}`
  const [visible, setVisible] = useState(
    sessionStorage.getItem(STORAGE_KEY) !== 'hidden',
  )
  const classes = useStyles()
  const theme = useTheme()
  const bannerRef = useRef(null)
  const titleWrapperRef = useRef(null)
  const titleRef = useRef(null)
  const [titleFontSize, setTitleFontSize] = useState(null)
  const { handleBannerHeightChange } = useBannerOffset()
  const { user, company } = useAuth()

  const isCountdown = variant === DPO_DAY_BANNER_COUNTDOWN_VARIANT

  useEffect(() => {
    if (onVisibilityChange) {
      onVisibilityChange(visible)
    }
  }, [visible, onVisibilityChange])

  // Emite a altura do banner sempre que visível ou redimensionado
  // Os menus respeitam o valor da altura do banner para manter responsivo e não quebrar nosso layout
  useEffect(() => {
    if (!handleBannerHeightChange) return

    const update = () => {
      if (!visible) {
        handleBannerHeightChange(0)
        return
      }
      const height = bannerRef.current?.getBoundingClientRect?.().height || 0
      handleBannerHeightChange(Math.ceil(height))
    }

    update()

    if (!visible) return

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update)
      if (bannerRef.current) observer.observe(bannerRef.current)
      window.addEventListener('resize', update)
      return () => {
        observer.disconnect()
        window.removeEventListener('resize', update)
      }
    } else {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
  }, [handleBannerHeightChange, visible])

  // Encolhe a fonte do título em vez de cortar o texto com reticências,
  // conforme o espaço disponível ao lado da logo diminui.
  useEffect(() => {
    const wrapper = titleWrapperRef.current
    const title = titleRef.current
    if (!wrapper || !title) return

    const update = () => {
      if (window.innerWidth < theme.breakpoints.values.sm) {
        setTitleFontSize(null)
        return
      }

      const containerWidth = wrapper.clientWidth
      const currentFontSize = titleFontSize || TITLE_MAX_FONT_SIZE
      const naturalWidthAtMax =
        (title.scrollWidth * TITLE_MAX_FONT_SIZE) / currentFontSize

      if (containerWidth <= 0 || naturalWidthAtMax <= 0) return

      if (naturalWidthAtMax > containerWidth) {
        const newFontSize = Math.max(
          (TITLE_MAX_FONT_SIZE * containerWidth) / naturalWidthAtMax,
          TITLE_MIN_FONT_SIZE,
        )
        setTitleFontSize(newFontSize)
      } else if (currentFontSize !== TITLE_MAX_FONT_SIZE) {
        setTitleFontSize(TITLE_MAX_FONT_SIZE)
      }
    }

    update()

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(update)
      observer.observe(wrapper)
      window.addEventListener('resize', update)
      return () => {
        observer.disconnect()
        window.removeEventListener('resize', update)
      }
    } else {
      window.addEventListener('resize', update)
      return () => window.removeEventListener('resize', update)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, titleFontSize])

  const handleClose = () => {
    setVisible(false)
    sessionStorage.setItem(STORAGE_KEY, 'hidden')

    if (handleBannerHeightChange) handleBannerHeightChange(0)

    helpers.mixpanel.track('Banner DPO Day', 'Fechar banner', {
      action: 'close_button_click',
      variant,
    })
  }

  const handleClick = () =>
    helpers.mixpanel.track('Banner DPO Day', 'Clique aqui', {
      action: 'clique_aqui_button_click',
      target: DPO_DAY_TARGET_URL,
      variant,
      tag: DPO_DAY_BANNER_MIXPANEL_TAG,
      user_id: user?.id,
      company_id: company?.id,
    })

  if (!visible) return null

  return (
    <Box className={classes.bannerRoot} ref={bannerRef}>
      <img
        src={DpoDayHexagon}
        alt="DPO Day 2027"
        className={classes.bannerHexagon}
      />

      <Box className={classes.bannerInfo}>
        <Box className={classes.bannerInfoTitleWrapper} ref={titleWrapperRef}>
          <Typography
            className={classes.bannerInfoTitle}
            ref={titleRef}
            style={titleFontSize ? { fontSize: titleFontSize } : undefined}
          >
            O maior evento de proteção de dados do Brasil
          </Typography>
        </Box>
        <Box className={classes.bannerInfoRow}>
          <Pill color={DPO_DAY_PILL_COLORS.PINK}>28 de Janeiro de 2027</Pill>
          <Box className={classes.bannerLocation}>
            <img
              src={DpoDayCuboLogo}
              alt="Cubo Itaú"
              className={classes.cuboLogo}
            />
            <Typography className={classes.bannerLocationText}>
              São Paulo-SP
            </Typography>
          </Box>
        </Box>
      </Box>

      {!isCountdown && (
        <Pill color={DPO_DAY_PILL_COLORS.GREEN}>
          Pré-venda <span className={classes.textBold}>EXCLUSIVA</span>
          <br />
          para <span className={classes.textBold}>CLIENTES</span> DPOnet
        </Pill>
      )}

      <Pill
        color={
          isCountdown ? DPO_DAY_PILL_COLORS.GREEN : DPO_DAY_PILL_COLORS.PINK
        }
        highlight="50%"
        className={classes.discountPill}
      >
        DE <span className={classes.textBold}>DESCONTO</span>
        <br />
        <span className={classes.textBold}>POR TEMPO LIMITADO</span>
      </Pill>

      {isCountdown && (
        <Pill
          color={DPO_DAY_PILL_COLORS.PINK}
          highlight={daysRemaining}
          textClassName={clsx(classes.countdownText, classes.textBold)}
        >
          {daysRemaining === 1 ? 'Dia' : 'Dias'}
          <br />
          {daysRemaining === 1 ? 'Restante' : 'Restantes'}
        </Pill>
      )}

      <Box
        component="a"
        href={DPO_DAY_TARGET_URL}
        target="_blank"
        rel="noopener noreferrer"
        className={classes.ctaButton}
        onClick={handleClick}
      >
        <Typography className={classes.ctaButtonText}>
          Clique e aproveite!
        </Typography>
        <Box className={classes.ctaIconWrapper}>
          <ChevronRight className={classes.ctaIcon} />
        </Box>
      </Box>

      <IconButton
        className={classes.bannerClose}
        onClick={handleClose}
        size="medium"
        aria-label="Fechar banner"
      >
        <CloseIcon />
      </IconButton>
    </Box>
  )
}

DpoDayBanner.propTypes = {
  variant: PropTypes.oneOf([
    constants.dpoday.DPO_DAY_BANNER_DEFAULT_VARIANT,
    constants.dpoday.DPO_DAY_BANNER_COUNTDOWN_VARIANT,
  ]).isRequired,
  daysRemaining: PropTypes.number,
  onVisibilityChange: PropTypes.func,
}

export default DpoDayBanner
